/**
 * Scenario Layers View component for viewing scenario reservoirs
 */
import {
    Box,
    Card,
    CardContent,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { ScenarioLayer } from '../api/scenarioLayers';
import { ScenarioResponse } from '../types/scenario';
import { ScenarioLayerDetailModal } from './ScenarioLayerDetailModal';

interface ScenarioLayersViewProps {
  scenarioId: string;
  layers: ScenarioLayer[];
  scenario: ScenarioResponse;
  onBack: () => void;
}

export const ScenarioLayersView: React.FC<ScenarioLayersViewProps> = ({
  scenarioId,
  layers,
  scenario,
  onBack,
}) => {
  const [selectedLayer, setSelectedLayer] = useState<ScenarioLayer | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const handleViewLayerDetails = (layer: ScenarioLayer) => {
    setSelectedLayer(layer);
    setDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedLayer(null);
  };

  const getLayerTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      oil: '#ff9800',
      gas: '#2196f3',
      oil_gas: '#9c27b0',
      water: '#00bcd4',
    };
    return colors[type] || '#757575';
  };

  const getSourceType = (layer: ScenarioLayer) => {
    if (layer.ref_layer_template) return 'Template';
    if (layer.ref_layer) return 'Existing';
    return 'Custom';
  };

  const getSourceName = (layer: ScenarioLayer) => {
    if (layer.layer_template) return layer.layer_template.name;
    if (layer.layer) return layer.layer.name;
    return 'Custom Layer';
  };

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {layers.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Reservoirs
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {layers.filter(l => l.ref_layer_template).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                From Templates
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                {layers.filter(l => !l.ref_layer_template && !l.ref_layer).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Custom Created
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Reservoirs Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Scenario Reservoirs
          </Typography>

          {layers.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No reservoirs added to this scenario yet.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Reservoir Name</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Properties</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {layers.map((layer) => (
                    <TableRow key={layer.scenario_layer_id}>
                      <TableCell>
                        <Typography
                          variant="body1"
                          fontWeight="medium"
                          onClick={() => handleViewLayerDetails(layer)}
                          sx={{
                            color: 'primary.main',
                            cursor: 'pointer',
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          {layer.layer_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {getSourceType(layer)}
                          </Typography>
                          <Typography variant="body2">
                            {getSourceName(layer)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {layer.properties.length} properties
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Layer Detail Modal */}
      {selectedLayer && (
        <ScenarioLayerDetailModal
          open={detailModalOpen}
          onClose={handleCloseDetailModal}
          layer={selectedLayer}
          readOnly={true}
        />
      )}
    </Box>
  );
};
